import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ArtworkAdmin, MetaPayload } from '../../shared/types';
import { adminListArtwork, deleteArtwork, fetchMeta } from '../lib/api';
import { supabase } from '../lib/supabase';
import { UploadDialog } from '../components/UploadDialog';
import { EditDialog } from '../components/EditDialog';

export function AdminDashboardPage() {
  const [items, setItems] = useState<ArtworkAdmin[]>([]);
  const [meta, setMeta] = useState<MetaPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<ArtworkAdmin | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const navigate = useNavigate();

  async function reload() {
    setLoading(true);
    try {
      const [list, m] = await Promise.all([adminListArtwork(), fetchMeta()]);
      setItems(list.items);
      setMeta(m);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    supabase.auth.getUser().then(({ data }) => setUserEmail(data.user?.email ?? null));
  }, []);

  const categorySuggestions = useMemo(
    () => meta?.categories.map((c) => c.name) ?? [],
    [meta],
  );
  const tagSuggestions = useMemo(() => meta?.tags.map((t) => t.name) ?? [], [meta]);

  async function onSignOut() {
    await supabase.auth.signOut();
    navigate('/admin', { replace: true });
  }

  async function onDelete(item: ArtworkAdmin) {
    const ok = window.confirm(`Delete "${item.title}"? This can't be undone.`);
    if (!ok) return;
    try {
      await deleteArtwork(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      // Refresh meta so empty categories/tags drop off the filters.
      fetchMeta().then(setMeta).catch(() => {});
    } catch (err) {
      alert(`Delete failed: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold">Admin dashboard</h1>
          <p className="mt-1 text-sm text-ink-500">
            Signed in{userEmail ? ` as ${userEmail}` : ''}.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={onSignOut}>
            Sign out
          </button>
          <button type="button" className="btn-primary" onClick={() => setUploadOpen(true)}>
            + New artwork
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-accent/30 bg-accent/5 p-4 text-sm text-accent">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {loading && items.length === 0 && (
          <div className="col-span-full py-10 text-center text-sm text-ink-500">
            Loading artworks…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-ink-900/15 p-10 text-center">
            <div className="font-display text-xl text-ink-700">No artworks yet</div>
            <p className="mt-1 text-sm text-ink-500">
              Click "New artwork" to upload your first piece.
            </p>
          </div>
        )}
        {items.map((item) => (
          <AdminCard
            key={item.id}
            item={item}
            onEdit={() => setEditing(item)}
            onDelete={() => onDelete(item)}
          />
        ))}
      </div>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={reload}
        categorySuggestions={categorySuggestions}
        tagSuggestions={tagSuggestions}
      />
      {editing && (
        <EditDialog
          artwork={editing}
          onClose={() => setEditing(null)}
          onSaved={reload}
          categorySuggestions={categorySuggestions}
          tagSuggestions={tagSuggestions}
        />
      )}
    </div>
  );
}

function AdminCard({
  item,
  onEdit,
  onDelete,
}: {
  item: ArtworkAdmin;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const aspect =
    item.thumb_width && item.thumb_height
      ? `${item.thumb_width} / ${item.thumb_height}`
      : '4 / 5';
  return (
    <div className="overflow-hidden rounded-2xl border border-ink-900/10 bg-white shadow-sm">
      <div className="relative bg-canvas-100" style={{ aspectRatio: aspect }}>
        {item.thumb_url ? (
          <img src={item.thumb_url} alt={item.title} className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center text-xs text-ink-500">
            No image
          </div>
        )}
        <span
          className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-xs font-medium ${
            item.status === 'ready'
              ? 'bg-white/90 text-ink-700'
              : item.status === 'pending'
                ? 'bg-accent-soft/90 text-ink-900'
                : 'bg-accent text-white'
          }`}
        >
          {item.status}
        </span>
      </div>
      <div className="p-4">
        <div className="font-display text-base font-semibold">{item.title}</div>
        <div className="mt-0.5 text-xs text-ink-500">
          {item.category.name || '—'}
          {item.tags.length > 0 ? ` • ${item.tags.map((t) => `#${t.name}`).join(' ')}` : ''}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" className="btn-secondary text-xs" onClick={onEdit}>
            Edit
          </button>
          <button type="button" className="btn-danger text-xs" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
